import {
    fetchScenario,
    getWidgetDataOrUpdatePage,
    selectBottomSheets,
    selectCurrentPageId,
    selectModalWindows,
    selectPlayerId,
    selectScenarioId,
    updateWidgetValueByInternalId,
    useAppDispatch,
    useAppSelector,
} from '@libs/store';
import {
    ActionActionTypeEnum,
    ActionPlayerTypeNameEnums,
    ActionTypeEnum,
    SystemEventTypeEnum,
    TCommonData,
    TPlayerActionData,
    TPlayerActionOption,
    TUseTriggerAction,
    TUseTriggerActionOptions,
    TWAction,
    TWidget,
} from '@libs/types';
import { copyTextToClipboard, eventBus, setLocale } from '@libs/utils';
import { useCallback } from 'react';
import { usePortal } from './app';
import { getGlobalVariables } from './getGlobalVariables';
import { addHistory } from './useNavigationBlockerViaHistory';
import { actionsCall, actionsCallByTypeAndName, isShowLoader } from './utils/triggerActionHelper';

declare const APP_TITLE: string;

export const useTriggerAction = <TD extends TCommonData>(widget?: TWidget<TD>, isCallForSystemAction?: boolean): TUseTriggerAction<TD> => {
    const { setBottomSheet, setModalWindow } = usePortal();
    const { redirectURLtoCloseFramework } = getGlobalVariables('redirectURLtoCloseFramework');

    const scenarioId = useAppSelector(selectScenarioId);
    const currentPageId = useAppSelector(selectCurrentPageId);
    const bottomSheets = useAppSelector(selectBottomSheets);
    const modalWindows = useAppSelector(selectModalWindows);
    const playerId = useAppSelector(selectPlayerId);
    const dispatch = useAppDispatch();

    const _triggerPlayerAction = useCallback(
        (action: TWAction, playerActionOptions?: TPlayerActionOption, value?: TPlayerActionData, widget?: TWidget<TCommonData>) => {
            const newValue = (value ?? widget?.data?.value) as string;

            switch (action.name) {
                case ActionPlayerTypeNameEnums.copy: {
                    const textToCopy = playerActionOptions?.textForCopyToClipboard ?? newValue;
                    copyTextToClipboard(textToCopy);
                    break;
                }
                case ActionPlayerTypeNameEnums.share:
                    if (navigator.share) {
                        const shareData: ShareData = {
                            title: APP_TITLE,
                            text: newValue,
                        };
                        navigator.share(shareData).catch(() => {
                            window.open(newValue, '_blank');
                        });
                    } else {
                        window.open(newValue, '_blank');
                    }
                    break;
                case ActionPlayerTypeNameEnums.showToast: {
                    const toastMessage = playerActionOptions?.toastMessage ?? newValue;
                    eventBus.emitGeneral(SystemEventTypeEnum.TOAST_MESSAGE, toastMessage);
                    break;
                }
                case ActionPlayerTypeNameEnums.closeFramework:
                    if (!process.env.NX_PUBLIC_EMBEDDED_PLAYER && redirectURLtoCloseFramework) {
                        if ('serviceWorker' in navigator) {
                            // stop serviceWorker for get page not from worker. We will start load this from server
                            navigator.serviceWorker
                                .getRegistrations()
                                .then((registrations) => {
                                    return Promise.all(registrations.map((registration) => registration.unregister()));
                                })
                                .catch((error) => {
                                    // Even if unregister fails, we still want to redirect.
                                    console.error('Service Worker unregistration failed: ', error);
                                })
                                .finally(() => {
                                    // we do not allow user to come back via browser navigation button
                                    window.location.replace(redirectURLtoCloseFramework);
                                });
                        } else {
                            // we do not allow user to come back via browser navigation button
                            window.location.replace(redirectURLtoCloseFramework);
                        }
                    }
                    break;
                case ActionPlayerTypeNameEnums.execute:
                    if (action.widgetId) {
                        eventBus.emit(SystemEventTypeEnum.EXECUTE, action.widgetId, newValue);
                    }
                    break;
                case ActionPlayerTypeNameEnums.changeLanguage:
                    setLocale(newValue);
                    break;
                default: {
                    if (action.actionType === ActionActionTypeEnum.closeBottomsheet || action.name === ActionPlayerTypeNameEnums.closeBottomsheet) {
                        setBottomSheet(undefined);
                    } else if (action.widgetId) {
                        const { widgetId } = action;
                        const modalWindow = modalWindows?.[widgetId];
                        const bottomSheet = bottomSheets?.[widgetId];

                        if (bottomSheet) {
                            setBottomSheet(widgetId);
                        } else if (modalWindow) {
                            setModalWindow(widgetId);
                        } else if (newValue !== undefined) {
                            dispatch(updateWidgetValueByInternalId({ internalId: widgetId, value: newValue }));
                        }
                    }
                }
            }
        },
        [bottomSheets, dispatch, modalWindows, redirectURLtoCloseFramework, setBottomSheet, setModalWindow],
    );

    const triggerAction = useCallback(
        async (action: TWAction, option?: TUseTriggerActionOptions): Promise<TWidget<TD> | undefined> => {
            if (!process.env.NX_PUBLIC_EMBEDDED_PLAYER && !isCallForSystemAction && action.type !== ActionTypeEnum.Player) {
                addHistory(action);
            }
            const wid = option?.widgetAction ?? widget;
            const showLoader = isShowLoader(action, option?.isShowLoader);

            if (action.type === ActionTypeEnum.Player) {
                _triggerPlayerAction(action, option?.playerActionOptions, option?.value, wid);
            } else if (action.type === ActionTypeEnum.Widget && wid) {
                try {
                    return await getWidgetDataOrUpdatePage(
                        action,
                        wid.uid,
                        wid.internalId,
                        scenarioId,
                        currentPageId,
                        dispatch,
                        option?.isUpdateWidgetByUid ?? true,
                        playerId,
                        showLoader,
                    );
                } catch (e) {
                    console.error(`Widget '${wid.id}':${wid.uid} catch error of fetch data:`, e);
                }
            } else {
                dispatch(fetchScenario({ action: action, widgetUid: widget?.uid, playerId, isShowLoader: showLoader }));
            }
            return Promise.resolve(undefined);
        },
        [_triggerPlayerAction, currentPageId, dispatch, isCallForSystemAction, playerId, scenarioId, widget],
    );

    const triggerActionsByType = useCallback(
        async (type: ActionActionTypeEnum | string, option?: TUseTriggerActionOptions) => {
            const promises = actionsCall(type, (a) => triggerAction(a, option), widget?.actions);
            const results = await Promise.all(promises);
            return results.find((result) => result !== undefined);
        },
        [triggerAction, widget?.actions],
    );

    const triggerActionsByNameAndType = useCallback(
        async (type: ActionActionTypeEnum | string, name: string, option?: TUseTriggerActionOptions) => {
            const promises = actionsCallByTypeAndName(type, name, (a) => triggerAction(a, option), widget?.actions);
            const results = await Promise.all(promises);
            return results.find((result) => result !== undefined);
        },
        [triggerAction, widget?.actions],
    );

    return {
        triggerAction,
        triggerActionsByType,
        triggerActionsByNameAndType,
    };
};
